import { useState } from "react"
import styled from "styled-components"

import { Banner, Text } from "@minuthq/meatball-ui-react"

import { ParadiseCountrySelector } from "src/components/Paradise/ParadiseCountrySelector"
import { ParadiseShippableProductsQuantityTable } from "src/components/Paradise/ParadiseShippableProductsQuantityTable"
import { useFetchShippingDestinations } from "src/data/ecommerce/ecommerceQueries"
import { useFetchParadiseShippableProducts } from "src/data/paradise/paradiseShippableProducts/paradiseShippableProductsQueries"
import { spacing } from "src/ui/spacing"

import { ParadiseAvailableShippingMethods } from "./ParadiseAvailableShippingMethods"
export function ParadiseShippingMethodChecker() {
  const [selectedShippingCountry, setSelectedShippingCountry] =
    useState<string>("")
  const [selectedShippableProducts, setSelectedShippableProducts] = useState<
    {
      id: string
      quantity: number
    }[]
  >([])

  const fetchShippingDestinations = useFetchShippingDestinations()

  const selectedShippingDestination = fetchShippingDestinations.data?.find(
    (destination) => destination.country_code === selectedShippingCountry
  )

  const fetchShippableProducts = useFetchParadiseShippableProducts({
    filter: {
      currency_code: selectedShippingDestination?.currency_code,
    },
    options: {
      enabled: !!selectedShippingDestination,
    },
  })

  const availableProducts =
    fetchShippableProducts.data?.shippable_products.filter(
      (product) => product.type === "additional"
    )

  return (
    <Wrapper>
      <Form>
        <FieldSet>
          <Text variant="subtitle">Shipping country</Text>

          <ParadiseCountrySelector
            label="Shipping country"
            name="shipping-country"
            countryCodes={
              fetchShippingDestinations.data?.map(
                (destination) => destination.country_code
              ) || []
            }
            selectedCountryCode={selectedShippingCountry}
            onSelectCountryCode={(countryCode) => {
              setSelectedShippingCountry(countryCode)
              setSelectedShippableProducts([])
            }}
          />
        </FieldSet>

        <FieldSet disabled={!selectedShippingCountry}>
          <Text variant="subtitle">Shippable products</Text>

          <ParadiseShippableProductsQuantityTable
            availableShippableProducts={
              availableProducts
                ? availableProducts.map((product) => ({
                    productId: product.product_id,
                    name: product.name,
                    imageUrl: product.image_url,
                  }))
                : []
            }
            selectedShippableProducts={selectedShippableProducts.map(
              (product) => ({
                productId: product.id,
                quantity: product.quantity,
              })
            )}
            updateShippableProducts={(shippableProducts) => {
              setSelectedShippableProducts(
                shippableProducts.map((product) => ({
                  id: product.productId,
                  quantity: product.quantity,
                }))
              )
            }}
            currency={selectedShippingDestination?.currency_code ?? "USD"}
            hideTotal
          />
        </FieldSet>
      </Form>
      <div>
        <Text variant="subtitle" marginBottom={spacing.S}>
          Available shipping methods
        </Text>

        {selectedShippableProducts.length > 0 && selectedShippingDestination ? (
          <ParadiseAvailableShippingMethods
            shippingCountry={selectedShippingDestination.country_code}
            currencyCode={selectedShippingDestination.currency_code}
            shippableProducts={selectedShippableProducts}
          />
        ) : (
          <Banner type="info">
            Please select a shipping country and shippable products to see
            available shipping methods
          </Banner>
        )}
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${spacing.L};
  max-width: 1400px;
`

const Form = styled.form`
  display: grid;
  gap: ${spacing.M};
`

const FieldSet = styled.fieldset`
  all: unset;

  display: flex;
  flex-direction: column;
  gap: ${spacing.M};

  transition: opacity 0.3s;

  &:disabled {
    opacity: 0.5;
  }
`
